import { type FC, type ReactNode } from "react";

import { SideSheet } from "../../common/SideSheet";
import { RetroEmpty } from "../retro";

export const SHEET_WIDTH = "w-full max-w-5xl";

/** One ancestor in the inspect-stack breadcrumb, oldest → newest. */
export interface BreadcrumbItem {
  label: string;
}

// ---------------------------------------------------------------------------
// InspectorToolbar — slim row above the tabs carrying the inspect-stack
// breadcrumb (`‹ #a › cur`) and any mode-specific control (e.g. the actor
// pivot). Renders nothing when there's no trail and no children, so the sheet
// stays flush in the common direct-open case.
// ---------------------------------------------------------------------------

export const InspectorToolbar: FC<{
  ancestors: BreadcrumbItem[];
  currentLabel: string;
  onJumpTo: (index: number) => void;
  children?: ReactNode;
}> = ({ ancestors, currentLabel, onJumpTo, children }) => {
  if (ancestors.length === 0 && !children) {
    return null;
  }
  const crumbClass =
    "max-w-[14rem] truncate font-mono text-[10px] font-bold uppercase tracking-[0.16em]";
  return (
    <div className="flex flex-shrink-0 items-center gap-2 border-b border-slate-200/70 px-3 py-1.5 dark:border-slate-800/70">
      {ancestors.length > 0 && (
        <nav
          aria-label="Inspector trail"
          className="flex min-w-0 items-center gap-1.5"
        >
          <button
            type="button"
            onClick={() => onJumpTo(ancestors.length - 1)}
            aria-label="Back"
            className="font-mono text-[12px] leading-none text-slate-500 transition-colors hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300"
          >
            ‹
          </button>
          {ancestors.map((ancestor, index) => (
            <span
              key={`${index}-${ancestor.label}`}
              className="flex min-w-0 items-center gap-1.5"
            >
              <button
                type="button"
                onClick={() => onJumpTo(index)}
                className={`${crumbClass} text-slate-500 transition-colors hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300`}
              >
                {ancestor.label}
              </button>
              <span
                aria-hidden
                className="text-[9px] text-slate-400 dark:text-slate-600"
              >
                ›
              </span>
            </span>
          ))}
          <span
            className={`${crumbClass} text-slate-900 dark:text-white`}
            aria-current="page"
          >
            {currentLabel}
          </span>
        </nav>
      )}
      {children}
    </div>
  );
};

// ---------------------------------------------------------------------------
// NotFoundInspector — explicit not-found state for a bad/deleted ?inspect=
// target. Never silently fall back to stale content.
// ---------------------------------------------------------------------------

export const NotFoundSheet: FC<{
  isOpen: boolean;
  onClose: () => void;
  ancestors: BreadcrumbItem[];
  onJumpTo: (index: number) => void;
  label: string;
  sub: string;
}> = ({ isOpen, onClose, ancestors, onJumpTo, label, sub }) => (
  <SideSheet
    isOpen={isOpen}
    onClose={onClose}
    title={label}
    widthClass={SHEET_WIDTH}
  >
    <div className="-mx-3 -my-2.5 flex h-[calc(100%+1.25rem)] flex-col sm:-mx-4 sm:-my-3 sm:h-[calc(100%+1.5rem)]">
      <InspectorToolbar
        ancestors={ancestors}
        currentLabel="—"
        onJumpTo={onJumpTo}
      />
      <div className="min-h-0 flex-1 p-6">
        <RetroEmpty label={label} sub={sub} />
      </div>
    </div>
  </SideSheet>
);
